<template>
  <div class="p-6">
    <div class="text-center mb-10" v-if="!inDialog">
      <h1 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('ChoosePlan') }}</h1>
      <div class="inline-block px-6 py-3 bg-gradient-to-r from-amber-400 via-orange-400 to-pink-500 rounded-full shadow-lg transform hover:scale-105 transition-transform duration-300">
        <p class="text-white font-bold text-base flex items-center gap-2">
          <span class="text-xl">🎁</span>
          {{ $t("PayHint") }}
          <span class="text-xl">✨</span>
        </p>
      </div>
    </div>

    <!-- Pricing Grid -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
      <div 
        v-for="plan in plans" 
        :key="plan.id" 
        class="relative border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 bg-white flex flex-col items-center group cursor-default"
        :class="{'border-indigo-500 ring-1 ring-indigo-500 shadow-md': plan.isRecommended, 'opacity-90 hover:opacity-100': !plan.isRecommended}"
      >
        <!-- Badge -->
        <div v-if="plan.isRecommended" class="absolute -top-3 bg-indigo-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
           Recommended
        </div>

        <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
           <img :src="VIP" class="w-8 h-8 object-contain" loading="lazy" />
        </div>

        <h3 class="text-lg font-bold text-gray-900 mb-1">{{ $t(plan.titleKey) }}</h3>
        
        <div class="flex flex-col items-center mb-4">
           <span class="text-3xl font-bold text-gray-900 leading-tight">{{ prices[plan.priceId]?.formattedPrice || plan.price }}</span>
           <span class="text-sm text-gray-500 mt-1">{{ $t(plan.timeKey) }}</span>
        </div>

        <p class="text-sm text-gray-500 text-center mb-6 min-h-[40px]">
           {{ $t(plan.hintKey) }}
        </p>

        <el-button 
           :type="plan.buttonType" 
           class="w-full !rounded-lg !h-10 !font-semibold" 
           :plain="!plan.isRecommended"
           :loading="isProcessing"
           @click="openCheckout(plan)"
        >
           {{ $t(plan.buttonKey || 'Subscribe') }}
        </el-button>
      </div>
    </div>

    <div class="mt-12 text-center border-t border-gray-100 pt-8">
      <p class="text-xs text-gray-400">Secure payment via Paddle · Cancel anytime</p>
    </div>
  </div>
</template>

<script>
import VIP from '@/assets/vip.png'

export default {
  name: 'PricePage',
  data() {
    return {
      VIP,
      loading: false, // Local semantic loading if needed, mostly utilizing parent or internal button loading
      fetchingPrices: false,
      checkingSubscription: false,
      isProcessing: false,
      prices: {},
      plans: [
        {
          id: 'annual',
          priceId: 'pri_01hv37ntpgqgf0nf4vhkzy6st5',
          price: '$16.99',
          titleKey: 'AnnualVIP',
          timeKey: 'AnnualVIPTime',
          hintKey: 'AnnualVIPHint',
          buttonType: 'primary',
          days: 365,
          isRecommended: true
        },
        {
          id: 'monthly',
          priceId: 'pri_01htq3vq91c18n974hrf84c17q',
          price: '$1.99',
          titleKey: 'MonthlyVIP',
          timeKey: 'MonthlyVIPTime',
          hintKey: 'MonthlyVIPHint',
          buttonType: 'primary',
          days: 31,
          isRecommended: false
        },
        {
          id: 'daily',
          priceId: 'pri_01htq3wm34engpcy5905fc3hwp',
          price: '$0.59',
          titleKey: 'DailyPlan',
          timeKey: 'DailyPlanTime',
          hintKey: 'DailyPlanHint',
          buttonKey: 'Pay',
          buttonType: 'primary',
          days: 1,
          isRecommended: false
        }
      ]
    }
  },
  props: {
    inDialog: Boolean
  },
  async mounted() {
    try {
      if (typeof Paddle !== 'undefined') {
          await this.initializePaddle();
          await this.fetchPrices();
      } else {
        console.warn("Paddle script not loaded");
      }
    } catch (error) {
      console.error('Init failure:', error);
    }
  },
  methods: {
    async initializePaddle() {
      // eslint-disable-next-line no-undef
      Paddle.Initialize({
        token: 'live_08ccd4dc3ab91634d0586e4b147',
        eventCallback: this.handlePaddleEvent
      })
    },
    async fetchPrices() {
      this.fetchingPrices = true
      try {
        const items = this.plans.map(plan => ({ priceId: plan.priceId, quantity: 1 }))
        // eslint-disable-next-line no-undef
        const result = await Paddle.PricePreview({ items })
        this.prices = result.data.details.lineItems.reduce((acc, item) => {
          acc[item.price.id] = {
            formattedPrice: item.formattedTotals.total,
            amount: item.totals.total
          }
          return acc
        }, {})
      } catch (error) {
        console.warn('Price fetch partial failure', error)
      } finally {
        this.fetchingPrices = false
      }
    },
    async openCheckout(plan) {
      this.isProcessing = true

      // GA4 begin_checkout 事件
      if (typeof window.gtag !== 'undefined') {
        const price = this.prices[plan.priceId]?.amount
          ? (this.prices[plan.priceId].amount / 100).toFixed(2)
          : parseFloat(plan.price.replace('$', ''));

        window.gtag('event', 'begin_checkout', {
          'currency': 'USD',
          'value': parseFloat(price),
          'items': [{
            'item_id': plan.priceId,
            'item_name': plan.titleKey,
            'price': parseFloat(price),
            'quantity': 1
          }]
        });
        console.log('GA4 begin_checkout tracked:', plan.titleKey);
      }

      try {
        // eslint-disable-next-line no-undef
        await Paddle.Checkout.open({
          items: [{ priceId: plan.priceId, quantity: 1 }],
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            locale: this.$i18n.locale
          }
        })
      } catch (error) {
        this.$message.error(this.$t('CheckoutError'))
      } finally {
        this.isProcessing = false
      }
    },
    handlePaddleEvent(data) {
      if (data.name === 'checkout.completed') {
        const item = data.data.items[0];
        const plan = this.plans.find(p => p.priceId === item.priceId);
        if (plan) {
           const expirationTime = new Date();
           expirationTime.setDate(expirationTime.getDate() + plan.days);
           localStorage.setItem('purchaseStatus', 'purchased');
           localStorage.setItem('expirationTime', expirationTime.toISOString());

           // Google Ads 转化跟踪
           if (typeof window.gtag !== 'undefined') {
             const transactionId = data.data.transactionId || data.data.id || Date.now().toString();
             const value = item.price?.unitPrice?.amount ? (item.price.unitPrice.amount / 100).toFixed(2) : 1.0;
             const currency = item.price?.unitPrice?.currencyCode || 'USD';

             window.gtag('event', 'conversion', {
               'send_to': 'AW-690455913/4ggqCMe9t4sbEOmKnskC',
               'value': parseFloat(value),
               'currency': currency,
               'transaction_id': transactionId
             });

             // GA4 purchase 事件 (推荐的电商事件)
             window.gtag('event', 'purchase', {
               'transaction_id': transactionId,
               'value': parseFloat(value),
               'currency': currency,
               'items': [{
                 'item_id': plan.priceId,
                 'item_name': plan.titleKey,
                 'price': parseFloat(value),
                 'quantity': 1
               }]
             });

             // GA4 自定义事件: subscription_success
             const trafficSource = sessionStorage.getItem('traffic_source') || 'direct';
             window.gtag('event', 'subscription_success', {
               'plan_id': plan.id,
               'plan_name': plan.titleKey,
               'plan_days': plan.days,
               'value': parseFloat(value),
               'currency': currency,
               'transaction_id': transactionId,
               'traffic_source': trafficSource
             });

             console.log('GA4 events tracked:', {
               event: 'purchase & subscription_success',
               transaction_id: transactionId,
               value: value,
               currency: currency,
               plan: plan.titleKey
             });
           }

           this.$message.success(this.$t('PurchaseSuccess'));
           // Could emit event to parent to close dialog or refresh state
           window.location.reload();
        }
      }
    }
  }
}
</script>

<style scoped>
/* Scoped overrides if Tailwind isn't enough */
</style>
